import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Alert, Slider, Divider,
  Chip, TextField, useTheme, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DownloadIcon from '@mui/icons-material/Download';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  readFileAsText, parseAnyGeoFile, detectDriftPoints, haversineDistance,
  parseCoordinates, parseCSVCoordinates, exportGeoJSON,
} from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import L from 'leaflet';

function GpsDriftCleaner() {
  const theme = useTheme();
  const [originalCoords, setOriginalCoords] = useState([]);
  const [timestamps, setTimestamps] = useState([]);
  const [error, setError] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // Filter settings
  const [maxSpeedKmh, setMaxSpeedKmh] = useState(200);
  const [maxSigma, setMaxSigma] = useState(3);
  const [minDistanceM, setMinDistanceM] = useState(2);
  const [enableSpeed, setEnableSpeed] = useState(true);
  const [enableSigma, setEnableSigma] = useState(true);
  const [enableMinDist, setEnableMinDist] = useState(true);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'No coordinates found in file');

      const fc = result.geoJson;
      const coords = [];
      const ts = [];
      for (const f of fc.features) {
        if (f.geometry?.type === 'LineString') {
          f.geometry.coordinates.forEach(c => coords.push(c));
        } else if (f.geometry?.type === 'Point') {
          coords.push(f.geometry.coordinates);
          const t = f.properties?.timestamp || f.properties?.time || null;
          if (t) ts.push(t);
        }
      }
      if (coords.length === 0) throw new Error('No coordinates found');
      setOriginalCoords(coords);
      setTimestamps(ts);
      setError('');
    } catch (err) {
      setError(`Failed to load: ${err.message}`);
    }
  }, []);

  const handlePasteLoad = useCallback(() => {
    try {
      const result = parseAnyGeoFile(pasteText);
      if (result.error || !result.geoJson) {
        // Fallback to plain coordinates
        const coords = parseCoordinates(pasteText);
        if (coords.length > 0) {
          setOriginalCoords(coords);
          setTimestamps([]);
          setError('');
          setShowPaste(false);
          return;
        }
        throw new Error(result.error || 'No valid coordinates found');
      }
      const coords = [];
      for (const f of result.geoJson.features) {
        if (f.geometry?.type === 'LineString') f.geometry.coordinates.forEach(c => coords.push(c));
        else if (f.geometry?.type === 'Point') coords.push(f.geometry.coordinates);
      }
      if (coords.length === 0) throw new Error('No coordinates found');
      setOriginalCoords(coords);
      setTimestamps([]);
      setError('');
      setShowPaste(false);
    } catch (err) {
      setError(err.message);
    }
  }, [pasteText]);

  const result = useMemo(() => {
    if (originalCoords.length === 0) return null;
    return detectDriftPoints(originalCoords, {
      maxSpeedKmh: enableSpeed ? maxSpeedKmh : Infinity,
      maxDeviationSigma: enableSigma ? maxSigma : Infinity,
      minDistanceM: enableMinDist ? minDistanceM : 0,
      timestamps,
    });
  }, [originalCoords, maxSpeedKmh, maxSigma, minDistanceM, enableSpeed, enableSigma, enableMinDist, timestamps]);

  // Build visualization GeoJSON
  const mapData = useMemo(() => {
    if (!result) return null;
    const features = [];

    // Cleaned route (green)
    if (result.cleaned.length > 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: result.cleaned },
        properties: { layer: 'cleaned' },
      });
    }

    // Original route (red dashed) — shown as overlay
    if (originalCoords.length > 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: originalCoords },
        properties: { layer: 'original' },
      });
    }

    // Removed points as red markers
    result.removed.forEach(r => {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: r.coord },
        properties: { layer: 'removed', reason: r.reason, index: r.index },
      });
    });

    return { type: 'FeatureCollection', features };
  }, [result, originalCoords]);

  const styleFunction = useCallback((feature) => {
    const layer = feature.properties?.layer;
    if (layer === 'cleaned') return { color: '#10b981', weight: 3.5, opacity: 0.9 };
    if (layer === 'original') return { color: '#ef4444', weight: 2, opacity: 0.5, dashArray: '6 4' };
    if (layer === 'removed') return { color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.8, radius: 5 };
    return { color: '#6366f1', weight: 2 };
  }, []);

  const pointToLayerFn = useCallback((feature, latlng) => {
    if (feature.properties?.layer === 'removed') {
      return L.circleMarker(latlng, {
        radius: 5, fillColor: '#ef4444', color: '#fff', weight: 1.5, fillOpacity: 0.9,
      }).bindTooltip(`#${feature.properties.index}: ${feature.properties.reason}`, { direction: 'top' });
    }
    return L.marker(latlng);
  }, []);

  const origDist = useMemo(() => {
    let d = 0;
    for (let i = 1; i < originalCoords.length; i++) d += haversineDistance(originalCoords[i - 1], originalCoords[i]);
    return d;
  }, [originalCoords]);

  const cleanDist = useMemo(() => {
    if (!result) return 0;
    let d = 0;
    for (let i = 1; i < result.cleaned.length; i++) d += haversineDistance(result.cleaned[i - 1], result.cleaned[i]);
    return d;
  }, [result]);

  const handleExport = useCallback(() => {
    if (!result) return;
    const fc = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: result.cleaned },
        properties: { name: 'Cleaned Route' },
      }],
    };
    exportGeoJSON(fc, 'geojson', 'cleaned_route');
  }, [result]);

  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(1)} m`;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="GPS Drift Cleaner | Remove Noisy Coordinates | UmbrellaCo"
        description="Clean and filter noisy GPS coordinate traces. Apply speed limits, sigma deviations, and distance filters to generate smooth routes online."
        keywords="gps drift cleaner, filter gps points, clean gpx data, remove noisy coordinates, spatial filter"
        url="/spatial-tools/gps-drift-cleaner"
        jsonLd={createSoftwareAppSchema(
          'GPS Drift Cleaner',
          'Clean and filter noisy GPS coordinate traces using intelligent algorithms.',
          'https://umbrellaco.in/spatial-tools/gps-drift-cleaner',
          ['GPS Data Cleaning', 'Drift Filtering', 'Speed Validation']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>GPS Drift Cleaner</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your GPS trace to automatically filter out noisy, drifting, or anomalous coordinates based on speed and deviation thresholds.
        </Typography>
      </Box>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={33} minSize={22}>
          <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto',
            bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
          }}>
            {/* Upload */}
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Load Trace</Typography>
              <Stack spacing={1}>
                <Button component="label" variant="contained" fullWidth size="small"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                  }}
                >
                  {originalCoords.length > 0 ? `Loaded (${originalCoords.length} points)` : 'Upload GeoJSON / CSV'}
                  <input type="file" hidden accept=".geojson,.json,.csv,.txt" onChange={handleFileUpload} />
                </Button>
                <Button variant="outlined" size="small" startIcon={<ContentPasteIcon />}
                  onClick={() => setShowPaste(!showPaste)}>
                  Paste Coordinates
                </Button>
              </Stack>

              {showPaste && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField multiline rows={4} fullWidth size="small"
                    placeholder="Paste coordinates (lat, lon per line) or GeoJSON..."
                    value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                  />
                  <Button variant="contained" size="small" fullWidth onClick={handlePasteLoad} sx={{ mt: 1 }}>
                    Load
                  </Button>
                </Box>
              )}

              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>

            <Divider />

            {/* Filter Controls */}
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                <CleaningServicesIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>Cleaning Filters</Typography>
              </Stack>

              {/* Speed filter */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked={enableSpeed} onChange={(e) => setEnableSpeed(e.target.checked)} size="small" />}
                  label={<Typography variant="body2" fontWeight={600}>Speed Filter</Typography>}
                />
                {enableSpeed && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <Slider value={maxSpeedKmh} onChange={(e, v) => setMaxSpeedKmh(v)}
                      min={10} max={500} step={10} size="small" sx={{ flex: 1 }}
                      disabled={timestamps.length === 0}
                    />
                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 60 }}>{maxSpeedKmh} km/h</Typography>
                  </Stack>
                )}
                {enableSpeed && timestamps.length === 0 && (
                  <Typography variant="caption" color="text.secondary">⚠ No timestamps — speed filter inactive</Typography>
                )}
              </Box>

              {/* Sigma filter */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked={enableSigma} onChange={(e) => setEnableSigma(e.target.checked)} size="small" />}
                  label={<Typography variant="body2" fontWeight={600}>Sigma Filter</Typography>}
                />
                {enableSigma && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <Slider value={maxSigma} onChange={(e, v) => setMaxSigma(v)}
                      min={1} max={5} step={0.5} size="small" sx={{ flex: 1 }}
                    />
                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 30 }}>{maxSigma}σ</Typography>
                  </Stack>
                )}
              </Box>

              {/* Min distance filter */}
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={<Switch checked={enableMinDist} onChange={(e) => setEnableMinDist(e.target.checked)} size="small" />}
                  label={<Typography variant="body2" fontWeight={600}>Min Distance</Typography>}
                />
                {enableMinDist && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <Slider value={minDistanceM} onChange={(e, v) => setMinDistanceM(v)}
                      min={0} max={50} step={1} size="small" sx={{ flex: 1 }}
                    />
                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 30 }}>{minDistanceM}m</Typography>
                  </Stack>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Results */}
            {result && (
              <>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Results</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {[
                      { label: 'Original Points', value: originalCoords.length },
                      { label: 'Cleaned Points', value: result.cleaned.length },
                      { label: 'Removed', value: result.removed.length, color: '#ef4444' },
                      { label: 'Reduction', value: `${((result.removed.length / originalCoords.length) * 100).toFixed(1)}%` },
                      { label: 'Original Dist', value: fmtDist(origDist) },
                      { label: 'Cleaned Dist', value: fmtDist(cleanDist) },
                    ].map(({ label, value, color }) => (
                      <Paper key={label} variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: color || 'inherit' }}>{value}</Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>

                {/* Removed points table */}
                {result.removed.length > 0 && (
                  <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2, pt: 1 }}>
                      Removed Points ({result.removed.length})
                    </Typography>
                    <TableContainer sx={{ flexGrow: 1, overflowY: 'auto', px: 1, maxHeight: '25vh' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.removed.slice(0, 100).map((r, i) => (
                            <TableRow key={i} sx={{ '& td': { py: 0.3, fontSize: '0.7rem' } }}>
                              <TableCell>{r.index}</TableCell>
                              <TableCell sx={{ color: '#ef4444' }}>{r.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                  <Button variant="contained" fullWidth size="small" startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                    }}
                  >
                    Export Cleaned Route
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        <Panel defaultSize={67} minSize={40}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {mapData ? (
              <MapView
                mapId="drift-cleaner-map"
                geoJsonData={mapData}
                styleFunction={styleFunction}
              />
            ) : (
              <Box sx={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              }}>
                <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', p: 4 }}>
                  <CleaningServicesIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload a GPS trace to clean</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Supports GeoJSON, CSV, or plain coordinates
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}



export default GpsDriftCleaner;
